import { When } from 'react-if'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { TheatricalRegionProvider } from '@/contexts/TheatricalRegionContext/TheatricalRegionContext'
import { Seo } from '@/layout/Seo'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { TheatricalFooter, TheatricalFooterProps } from '@/organisms/TheatricalFooter'
import { getTheatricalReleaseBySlug } from '@/services/CmsService/TheatricReleases'
import { mapSeoProps } from '@/services/ContentCatalog'
import { getLightFranchiseBySlug, LightFranchise } from '@/services/FranchiseService'
import { getProjectLivestreams, PlayableLivestream } from '@/services/LivestreamService'
import { isSellingTickets, inTheaters, isStreaming } from '@/services/ProjectPhaseService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import {
  getProjectAbout,
  getWatchableContentByGuid,
  ProjectMeta,
  WatchableContentWithReasons,
} from '@/services/ProjectsService'
import { getHubPageTheatricalRelease, HubPageTheatricalReleaseResult } from '@/services/Theatrical/queries'
import {
  getCurrentYear,
  getTheatricalPreReleasePageData,
  GetTheatricalPreReleasePageDataResult,
  isProjectThatsBothSeriesAndMovie,
  useMoviesTitle,
} from '@/services/TheatricalPreReleaseService'
import { getWhereToWatch, WhereToWatch } from '@/services/WhereToWatchService'
import { ProjectTheme, TheatricalRegion } from '@/types/codegen-contentful'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchTheatricalView } from '@/views/WatchTheatricalView'
import { WatchTheatricalContextProvider } from '@/views/WatchTheatricalView/WatchTheatricalContext'

interface MoviesPageProps extends ProjectMeta {
  allTheatricalReleaseRegions: TheatricalRegion[]
  inTheaters: boolean
  isStreaming: boolean
  livestreams: PlayableLivestream[]
  locale: string
  pageData: GetTheatricalPreReleasePageDataResult
  whereToWatch: WhereToWatch
  contentfulProjectTheme: ProjectTheme | null
  supportedTheatricalRegions: TheatricalRegion[]
  canShowTickets?: boolean
  ticketStatus?: TicketsStatusReference | null
  franchise: LightFranchise | null
  projectMeta: ProjectMeta
  watchableContent: WatchableContentWithReasons
  hubPageTheatricalRelease: HubPageTheatricalReleaseResult | null
}

export const MoviesPage: NextPageWithLayout<MoviesPageProps> = (props) => {
  const { filmDetails } = props.pageData
  const { inTheaters, isStreaming, locale, franchise, projectMeta } = props
  const shouldHide = shouldHideProjectFromSearchEngines(props)

  const path = `${paths.movies.index}/${props.slug}`
  const title = useMoviesTitle({
    filmDetails,
    filmName: projectMeta.name,
    inTheaters,
    isStreaming:
      props.slug === slugs.cabrini ||
      props.slug === slugs.sight ||
      props.slug === slugs.theBlind ||
      props.slug === slugs.theWay ||
      props.slug === slugs.redemptionOfHenryMyers ||
      props.slug === slugs.sweetwater ||
      props.slug === slugs.surprisedByOxford ||
      props.slug === slugs.somethingToStandForWithMikeRowe ||
      isStreaming,
    releaseYear: filmDetails?.releaseYear ?? getCurrentYear(),
    releaseDate: filmDetails?.theatricalReleaseDate ?? undefined,
  })

  return (
    <>
      <Seo
        {...mapSeoProps({
          path,
          title,
          locale,
          projectMeta,
          shortDescription: projectMeta.title?.shortDescription ?? undefined,
          landscapeBackdropPath: projectMeta.title?.landscapeBackdrop?.cloudinaryPath ?? undefined,
        })}
        noindex={shouldHide}
        nofollow={shouldHide}
        path={path}
      />
      <ProjectMetaProvider project={props}>
        <TheatricalRegionProvider
          supportedTheatricalRegions={props.supportedTheatricalRegions}
          regionData={props.allTheatricalReleaseRegions}
        >
          <WatchTheatricalContextProvider pageData={props.pageData} franchise={franchise}>
            <MarketingVideoPopup />
            <WatchTheatricalView
              livestreams={props.livestreams}
              whereToWatch={props.whereToWatch}
              contentfulProjectTheme={props.contentfulProjectTheme}
              isStreaming={props.isStreaming}
              watchableContent={props.watchableContent}
              hubPageTheatricalRelease={props.hubPageTheatricalRelease}
            />
            <When condition={props.slug === slugs.soundOfFreedom || props.slug === slugs.theShift}>
              <TheatricalFooter {...(filmDetails?.filmAssociationLogos as TheatricalFooterProps)} slug={props.slug} />
            </When>
          </WatchTheatricalContextProvider>
        </TheatricalRegionProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<MoviesPageProps>(
  { filePath: 'pages/[locale]/movies/[projectSlug].tsx' },
  async ({ federationClient, locale, params, preview, next404 }) => {
    const projectSlug = getItemOrFirstEntry(params.projectSlug)
    if (!projectSlug) return next404()

    const project = projectSlug.toLowerCase()

    const projectMeta = await getProjectAbout({ slug: project }, federationClient, {
      locale,
      preview,
      includeStills: true,
    })
    if (!projectMeta) {
      return {
        redirect: {
          destination: paths.movies.index,
          permanent: false,
        },
      }
    }

    const shouldIncludePreview = isDevelopmentEnvironment()
    const [
      livestreams,
      pageData,
      theatricalRelease,
      hubPageTheatricalRelease,
      whereToWatch,
      contentfulProjectTheme,
      watchableContent,
      translations,
    ] = await Promise.all([
      getProjectLivestreams(projectMeta, project, federationClient),
      getTheatricalPreReleasePageData({ slug: project }, federationClient, { locale, preview }),
      getTheatricalReleaseBySlug({
        theatricalSlug: project,
        opts: { preview: shouldIncludePreview, locale },
      }),
      getHubPageTheatricalRelease({
        theatricalSlug: project,
        client: federationClient,
      }),
      getWhereToWatch(project, preview),
      getContentfulProjectTheme(project, preview),
      getWatchableContentByGuid(projectMeta?.title?.id, federationClient),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'theatrical-presales',
        'pif',
        'guild',
      ]),
    ])

    const ticketStatus = hubPageTheatricalRelease?.region?.ticketsStatus ?? null
    const canShowTickets = isSellingTickets(ticketStatus)

    const allTheatricalReleaseRegions = theatricalRelease?.regionCollection || []
    const supportedTheatricalRegions = theatricalRelease?.supportedTheatricalRegions || []

    let franchise
    if (projectMeta.franchiseDetails?.slug) {
      franchise = await getLightFranchiseBySlug(projectMeta.franchiseDetails.slug)
    }

    const redirect = getRedirect(project)
    if (redirect) return redirect

    if (projectMeta?.projectType !== 'movie' && !isProjectThatsBothSeriesAndMovie(projectMeta)) {
      return {
        redirect: {
          destination: paths.movies.index,
          permanent: false,
        },
      }
    }

    return {
      props: {
        allTheatricalReleaseRegions,
        inTheaters: inTheaters(ticketStatus),
        isStreaming: isStreaming(watchableContent),
        livestreams,
        locale,
        pageData,
        supportedTheatricalRegions,
        whereToWatch,
        contentfulProjectTheme,
        canShowTickets,
        ticketStatus,
        franchise: omitUndefineds(franchise) ?? null,
        projectMeta,
        watchableContent,
        hubPageTheatricalRelease,
        ...projectMeta,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default MoviesPage

const getRedirect = (project: string) => {
  if (project === 'the-last-rodeo') {
    return {
      redirect: {
        destination: paths.movies.theLastRodeo,
        permanent: false,
      },
    }
  }

  if (project === 'angels-sing') {
    return {
      redirect: {
        destination: paths.watch.index,
        permanent: false,
      },
    }
  }

  if (project === slugs.dryBar) {
    return {
      redirect: {
        destination: paths.shows.dryBar,
        permanent: false,
      },
    }
  }

  if (project === slugs.truthAndConviction) {
    return {
      redirect: {
        destination: paths.movies.truthAndTreason,
        permanent: false,
      },
    }
  }

  if (project === slugs.truthAndTreasonSeries) {
    return {
      redirect: {
        destination: paths.shows.truthAndTreasonSeries,
        permanent: false,
      },
    }
  }

  return null
}
